import { createMuiTheme } from "@material-ui/core/styles";

// Primary Colors
const AZURE = "#3E7EF8";
const CULTURED = "#F4F6F8";
const OXFORD_BLUE = "#0D2962";

// Secondary Colors
const MEDIUM_GREEN = "#3BB273";
const LIGHT_BLUE = "#D8E2F7";
const SAND_BROWN = "#F4A261";
const DARK_GREEN = "#244F26";
const BURNT_SIENNA = "#E76F51";
const OLIVE = "#788475";
const BLUE_MOONSTONE = "#50648A";

// Base Colors
const JET = "#2E2E2E";
const BODY_GRAY = "#3B3B3B";
const DAVYS_GRAY = "#565656";
const GRAY_WEB = "#7D7E7E";
const SILVER = "#A4A6A7";
const LIGHT_GRAY = "#CCCECF";
const BASE = "#F4F6F8";

const MIRAGE = "#19202B";
const EBONY_CLAY = "#1E2533";
const PERIWINKLE_GRAY = "#CAD4E8";
const LIGHT_BLUE_GRAY = "#ACC6FD";
const DARK_BLUE_GRAY = "#405b93";

const COMET = "#5C6066";

// Common
const WHITE = "#FFF";
const BLACK = "#000";

// Cloud Connectors SVGs Colors
const AWS_DARK_TXT = "#252F3E";
const AWS_YELLOW_SMILE = "#FF9900";
const DUO_GREEN_1 = "#7bcd54";
const DUO_GREEN_2 = "#63c43f";
const M365_DARK_TXT = "#737373";
const M365_SQ1 = "#f25022";
const M365_SQ2 = "#7fba00";
const M365_SQ3 = "#00a4ef";
const M365_SQ4 = "#ffb900";
const S1_DARK_TXT = "#040404";
const S1_PURPLE = "#6b0aea";
const MIMECAST_DARK = "#080444";
const WEBROOT_GREEN = "#408740";
const UMBRELLA_BLUE = "#0d274d";

// Alert Colors
const CHAMBRAY = "#315290";
const TROPICAL_BLUE = "#CEDDF7";
const JORDY_BLUE = "#97B9F7";
const CERULEAN_BLUE = "#305FB7";
const IRISH_COFFEE = "#5C2C20";
const LINEN = "#FCF0ED";
const CELLO = "#1E3560";

const blumiraDefault = {
  palette: {
    common: {
      white: WHITE,
    },
    primary: {
      main: AZURE,
      dark: OXFORD_BLUE,
      light: CULTURED,
    },
    secondary: {
      main: OLIVE,
    },
    error: {
      main: BURNT_SIENNA,
    },
    warning: {
      main: SAND_BROWN,
    },
    info: {
      main: LIGHT_BLUE,
    },
    success: {
      main: MEDIUM_GREEN,
      dark: DARK_GREEN,
    },
    grey: {
      50: BASE,
      100: LIGHT_GRAY,
      200: LIGHT_GRAY,
      300: LIGHT_GRAY,
      400: SILVER,
      500: GRAY_WEB,
      600: DAVYS_GRAY,
      700: BODY_GRAY,
      800: JET,
      900: BLACK,
    },
    text: {
      primary: BODY_GRAY,
      secondary: GRAY_WEB,
      autoIsoControlBar: LIGHT_BLUE_GRAY,
    },
    background: {
      paper: WHITE,
      default: BASE,
      autoIsoControlBar: OXFORD_BLUE,
    },
    action: {
      toggleButtonHover: LIGHT_GRAY,
      disbaled: "rgba(86, 86, 86, 0.7)", // DAVYS_GRAY with a slightly decreased opacity
    },
    border: {
      autoIsoControlBar: DARK_BLUE_GRAY,
    },
  },
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    h1: {
      fontFamily: '"Prompt", sans-serif',
      fontWeight: "bold",
      fontSize: "60px",
      letterSpacing: "16",
    },
    h2: {
      fontFamily: '"Prompt", sans-serif',
      fontWeight: "bold",
      fontSize: "48px",
      letterSpacing: "16",
    },
    h3: {
      fontFamily: '"Prompt", sans-serif',
      fontWeight: "bold",
      fontSize: "40px",
      letterSpacing: "16",
    },
    h4: {
      fontFamily: '"Prompt", sans-serif',
      fontWeight: "bold",
      fontSize: "30px",
      letterSpacing: "16",
    },
    h5: {
      fontFamily: '"Prompt", sans-serif',
      fontWeight: "bold",
      fontSize: "24px",
      letterSpacing: "16",
    },
    h6: {
      fontFamily: '"Prompt", sans-serif',
      fontWeight: "bold",
      fontSize: "18px",
      letterSpacing: "16",
    },
  },
  shape: {
    padding: 15,
    borderRadius: 10,
  },
  props: {
    MuiInputLabel: {
      shrink: true,
    },
    MuiInput: {
      disableUnderline: true,
    },
  },
  overrides: {
    MuiTooltip: {
      tooltip: {
        fontSize: 13,
      },
    },
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: AZURE,
      },
    },
    MuiInputBase: {
      root: {
        "&.Mui-disabled": {
          borderColor: "rgba(154, 177, 219, 0.4)",
          color: "rgba(86, 86, 86, 0.7)", // DAVYS_GRAY with a slightly decreased opacity
          cursor: "not-allowed",
        },
      },
      input: {
        "&.Mui-disabled": {
          cursor: "not-allowed",
        },
      },
    },
    MuiFormLabel: {
      root: {
        "&.Mui-disabled": {
          color: "rgba(86, 86, 86, 0.7)",
        },
      },
    },
    MuiFormHelperText: {
      root: {
        paddingLeft: 7,
        "&.Mui-disabled": {
          color: "rgba(86, 86, 86, 0.7)",
        },
      },
    },
    MuiInput: {
      root: {
        borderRadius: 10,
        padding: "5px 10px",
        backgroundColor: WHITE,
        border: `1px solid lightgray`,
        "& svg": {
          color: "lightgray",
        },
        "&.Mui-focused": {
          borderColor: AZURE,
        },
      },
    },
    MuiButton: {
      root: {
        textTransform: "none",
      },
    },
    MuiAutocomplete: {
      input: {
        cursor: "pointer",
      },
      inputRoot: {
        cursor: "pointer",
        "&.MuiInput-root": {
          minHeight: "44px",
          paddingTop: "2px",
          paddingBottom: "2px",
        },
      },
    },
    MuiChip: {
      root: {
        "& svg.MuiChip-deleteIcon": {
          color: BLACK,
          opacity: 0.3,

          "&:hover": {
            opacity: 0.7,
          },
        },

        "& svg.MuiChip-deleteIcon.MuiChip-deleteIconColorPrimary": {
          color: WHITE,
          opacity: 0.6,

          "&:hover": {
            opacity: 1,
          },
        },
      },
    },
    MuiAlert: {
      standardInfo: {
        color: CHAMBRAY,
        borderColor: JORDY_BLUE,
        borderStyle: "solid",
        borderWidth: 1,
        backgroundColor: TROPICAL_BLUE,
        marginBottom: 20,

        "& .MuiAlert-icon": {
          color: CHAMBRAY,
        },
      },
      standardError: {
        color: IRISH_COFFEE,
        backgroundColor: LINEN,

        "& .MuiAlert-icon": {
          color: IRISH_COFFEE,
        },
      },
    },
    MuiRadio: {
      colorSecondary: {
        "&$checked": {
          color: AZURE,
        },
      },
    },
    MuiAccordion: {
      root: {
        padding: 15,
        boxShadow: "none",
        backgroundColor: WHITE,
      },
    },
    MuiToggleButton: {
      root: {
        border: `1px solid ${LIGHT_GRAY}`,
        "&.Mui-selected": {
          backgroundColor: LIGHT_GRAY,
        },
      },
      label: {
        color: BLACK,
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 5,
      },
      bar: {
        borderRadius: 5,
      },
    },
    MuiTablePagination: {
      select: {
        "&:focus": {
          border: `2px solid ${AZURE}`,
          backgroundColor: BASE,
          borderRadius: 2,
        },
      },
    },
  },
  svgs: {
    aws: {
      txt: {
        color: AWS_DARK_TXT,
      },
      smile: {
        color: AWS_YELLOW_SMILE,
      },
    },
    duo: {
      green1: {
        color: DUO_GREEN_1,
      },
      green2: {
        color: DUO_GREEN_2,
      },
    },
    m365: {
      txt: {
        color: M365_DARK_TXT,
      },
      sq1: {
        color: M365_SQ1,
      },
      sq2: {
        color: M365_SQ2,
      },
      sq3: {
        color: M365_SQ3,
      },
      sq4: {
        color: M365_SQ4,
      },
    },
    sentinel: {
      txt: {
        color: S1_DARK_TXT,
      },
      logo: {
        color: S1_PURPLE,
      },
    },
    mimecast: {
      txt: {
        color: MIMECAST_DARK,
      },
    },
    webroot: {
      txt: {
        color: WEBROOT_GREEN,
      },
    },
    umbrella: {
      color: UMBRELLA_BLUE,
    },
  },
};

const blumiraDark = {
  ...blumiraDefault,
  palette: {
    ...blumiraDefault["palette"],
    text: {
      primary: PERIWINKLE_GRAY,
      secondary: LIGHT_GRAY,
      autoIsoControlBar: BASE,
    },
    divider: "rgba(255, 255, 255, 0.12)",
    background: {
      paper: MIRAGE,
      default: EBONY_CLAY,
      autoIsoControlBar: AZURE,
    },
    border: {
      autoIsoControlBar: LIGHT_BLUE_GRAY,
    },
    action: {
      active: BLUE_MOONSTONE,
      hover: "rgba(154, 177, 219, 0.08)",
      hoverOpacity: "0.08",
      selected: "rgba(154, 177, 219, 0.16)",
      selectedOpacity: "0.16",
      disabled: "rgba(154, 177, 219, 0.7)", // #9ab1db with a slightly decreased opacity
      disabledBackground: "rgba(154, 177, 219, 0.12)",
      disabledOpacity: "0.38",
      focus: "rgba(154, 177, 219, 0.12)",
      focusOpacity: "0.12",
      activatedOpacity: "0.24",
      toggleButtonHover: BLUE_MOONSTONE,
    },
  },
  overrides: {
    ...blumiraDefault["overrides"],
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: OXFORD_BLUE,
      },
    },
    MuiInput: {
      root: {
        ...blumiraDefault["overrides"]["MuiInput"]["root"],
        backgroundColor: MIRAGE,
      },
    },
    MuiInputBase: {
      root: {
        "&.Mui-disabled": {
          borderColor: "rgba(154, 177, 219, 0.4)",
          color: "rgba(154, 177, 219, 0.7)", // #9ab1db with a slightly decreased opacity
          cursor: "not-allowed",
        },
      },
    },
    MuiFormLabel: {
      root: {
        "&.Mui-disabled": {
          color: "rgba(154, 177, 219, 0.7)",
        },
      },
    },
    MuiFormHelperText: {
      root: {
        paddingLeft: 7,
        "&.Mui-disabled": {
          color: "rgba(154, 177, 219, 0.7)",
        },
      },
    },
    MuiAlert: {
      standardInfo: {
        ...blumiraDefault["overrides"]["MuiAlert"]["standardInfo"],
        color: WHITE,
        borderColor: CERULEAN_BLUE,
        backgroundColor: CELLO,

        "& .MuiAlert-icon": {
          color: WHITE,
        },
      },
    },
    MuiSwitch: {
      track: {
        backgroundColor: COMET,
        opacity: "1",
      },
    },
    MuiAccordion: {
      root: {
        ...blumiraDefault["overrides"]["MuiAccordion"]["root"],
        backgroundColor: MIRAGE,
      },
    },
    MuiToggleButton: {
      root: {
        border: `1px solid ${BLUE_MOONSTONE}`,
        "&.Mui-selected": {
          backgroundColor: BLUE_MOONSTONE,
        },
      },
      label: {
        color: WHITE,
      },
    },
    MuiTablePagination: {
      select: {
        "&:focus": {
          border: `2px solid ${AZURE}`,
          backgroundColor: EBONY_CLAY,
          borderRadius: 2,
        },
      },
    },
  },
  svgs: {
    aws: {
      ...blumiraDefault["svgs"]["aws"],
      txt: {
        color: WHITE,
      },
    },
    duo: {
      ...blumiraDefault["svgs"]["duo"],
    },
    m365: {
      ...blumiraDefault["svgs"]["m365"],
      txt: {
        color: WHITE,
      },
    },
    sentinel: {
      ...blumiraDefault["svgs"]["sentinel"],
      txt: {
        color: WHITE,
      },
    },
    mimecast: {
      txt: {
        color: WHITE,
      },
    },
    webroot: {
      txt: {
        color: WHITE,
      },
    },
    umbrella: {
      color: WHITE,
    },
  },
};

export const defaultTheme = createMuiTheme(blumiraDefault);
export const shadowTheme = createMuiTheme(blumiraDark);
